import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { Editor } from "primereact/editor";

import DashboardLayout from "./components/Layout";
import useRole from "./components/useRole";
import { createTicketType, getTicketTypeById, updateTicketType } from "../../api/ticket-types";
import { getEventByIdAdmin } from "../../api/plan";
import { getVenueByIdAdmin } from "../../api/venue";
import { currentSeatPlan, formatSeatNumbers, parseSeatNumbers, seatPlanKey } from "../../components/SeatMap/pricing";

const TicketTypeFormPage = () => {
  const { eventId, id } = useParams();
  const { isAdmin } = useRole();
  const isEdit = !!id;
  const navigate = useNavigate();
  // Seat setup, only for events with a venue: { venue, plan, ticketTypes, initialKeys, hasPlan }
  const [seatSetup, setSeatSetup] = useState(null);
  // Seats of this ticket type, one row per zone: [{ zone: "A", text: "1-16" }]
  const [seatRows, setSeatRows] = useState([]);
  const [submitError, setSubmitError] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { isSubmitting },
  } = useForm();

  const isTable = watch("isTable");

  useEffect(() => {
    const loadSeats = async () => {
      const event = (await getEventByIdAdmin(eventId))?.data?.data;
      if (!event?.venueId) return;
      const venue = (await getVenueByIdAdmin(event.venueId))?.data?.data;
      const ticketTypes = event.ticketTypes || [];
      // Keep this type's seats even while it is inactive (inactive types are not in the event data)
      const knownTypes = isEdit && !ticketTypes.some(({ _id }) => _id === id) ? [...ticketTypes, { _id: id }] : ticketTypes;
      const plan = currentSeatPlan(venue, knownTypes, event.seatPlan);
      const ownKeys = isEdit ? Object.keys(plan).filter((key) => plan[key] === id) : [];

      setSeatSetup({ venue, plan, ticketTypes, initialKeys: [...ownKeys].sort(), hasPlan: Boolean(event.seatPlan) });
      setSeatRows(
        (venue?.zones || [])
          .map((zone) => ({
            zone: zone.code,
            text: formatSeatNumbers(zone.seats.map(({ number }) => number).filter((number) => ownKeys.includes(seatPlanKey(zone.code, number)))),
          }))
          .filter((row) => row.text)
      );
    };
    loadSeats();
  }, [eventId, id, isEdit]);

  useEffect(() => {
    if (isEdit) {
      getTicketTypeById(eventId, id).then((res) => {
        const data = res?.data?.data;
        if (data) {
          setValue("name", data.name.az);
          setValue("price", data.price);
          setValue("salePrice", data.salePrice);
          setValue("description", data.description.az);
          setValue("count", data.count);
          setValue("capacity", data.capacity);
          setValue("isTable", Boolean(data.isTable));
          setValue("isActive", data.isActive);
        }
      });
    }
  }, [isEdit, setValue, eventId, id]);

  // Seat keys from the rows, with per-row errors and what saving would change
  const seatSelection = useMemo(() => {
    if (!seatSetup) return null;
    const zonesByCode = Object.fromEntries((seatSetup.venue?.zones || []).map((zone) => [zone.code, zone]));
    const keys = new Set();
    const rowErrors = {};
    const rowCounts = {};
    seatRows.forEach((row, i) => {
      const zone = zonesByCode[row.zone];
      if (!zone) {
        if (row.text.trim()) rowErrors[i] = "Zona seçin";
        return;
      }
      const { numbers, error } = parseSeatNumbers(row.text);
      if (error) {
        rowErrors[i] = error;
        return;
      }
      const existing = new Set(zone.seats.map(({ number }) => number));
      const missing = numbers.filter((number) => !existing.has(number));
      if (missing.length) {
        rowErrors[i] = `${zone.name || zone.code} zonasında ${formatSeatNumbers(missing)} nömrəli yer yoxdur`;
        return;
      }
      rowCounts[i] = numbers.length;
      numbers.forEach((number) => keys.add(seatPlanKey(zone.code, number)));
    });

    const sortedKeys = [...keys].sort();
    const typeNames = Object.fromEntries(seatSetup.ticketTypes.map((type) => [type._id, type.name?.az]));
    const moving = {};
    sortedKeys.forEach((key) => {
      const owner = seatSetup.plan[key];
      if (owner && owner !== id) moving[typeNames[owner] || "başqa növ"] = (moving[typeNames[owner] || "başqa növ"] || 0) + 1;
    });

    return {
      keys: sortedKeys,
      rowErrors,
      rowCounts,
      moving,
      removed: seatSetup.initialKeys.filter((key) => !keys.has(key)).length,
      changed: JSON.stringify(sortedKeys) !== JSON.stringify(seatSetup.initialKeys),
    };
  }, [seatSetup, seatRows, id]);

  // Editing the seats clears the previous save error
  const changeRows = (update) => {
    setSubmitError(null);
    setSeatRows(update);
  };
  const updateRow = (index, changes) => changeRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  const onSubmit = async (formData) => {
    setSubmitError(null);
    if (seatSelection && Object.keys(seatSelection.rowErrors).length) {
      setSubmitError("Yerlərdə səhv var, zəhmət olmasa düzəldin.");
      return;
    }

    const payload = {
      name: { az: formData.name },
      price: Number(formData.price),
      salePrice: formData.salePrice ? Number(formData.salePrice) : undefined,
      description: { az: formData.description },
      count: formData.count ? Number(formData.count) : undefined,
      isTable: Boolean(formData.isTable),
      // null clears the seat count when the type is no longer a table
      capacity: formData.isTable && formData.capacity ? Number(formData.capacity) : null,
      isActive: formData.isActive,
    };
    // Only send seats when they changed, so saving other fields never touches the seat plan
    if (seatSelection?.changed) {
      payload.seats = seatSelection.keys;
    }

    try {
      if (isEdit) {
        await updateTicketType(eventId, id, payload);
      } else {
        await createTicketType(eventId, payload);
      }
    } catch (error) {
      setSubmitError(error?.response?.data?.message || "Yadda saxlamaq olmadı.");
      return;
    }

    navigate(`/dashboard/${eventId}/ticket-types`);
  };

  const zones = (seatSetup?.venue?.zones || []).filter((zone) => (zone.kind === "tables") === Boolean(isTable));
  const movingText = seatSelection
    ? Object.entries(seatSelection.moving).map(([name, count]) => `${count} yer ${name}`).join(", ")
    : "";
  if (!isAdmin) return <Navigate to={`/dashboard/${eventId}`} replace />;


  return (
    <DashboardLayout id={eventId}>
      <div className="max-w-2xl mx-auto mt-10 bg-white p-8 rounded-xl shadow">
        <h2 className="text-2xl font-bold mb-6">
          {isEdit ? "🎟️ Bilet Növünü Düzəlt" : "➕ Yeni Bilet Növü Əlavə Et"}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Adı (AZ)</label>
            <input
              {...register("name", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Cari Qiymət (₼)</label>
            <input
              type="number"
              step="0.01"
              {...register("price", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Əvvəlki qiymət (₼)</label>
            <input
              type="number"
              step="0.01"
              {...register("salePrice")}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Açıqlama</label>

            <Controller
              name="description"
              control={control}
              defaultValue=""
              rules={{ required: "Description is required" }}
              render={({ field }) => (
                <Editor
                  value={field.value}
                  onTextChange={(e) => field.onChange(e.htmlValue)}
                  style={{ height: '320px' }}
                />
                )}
              />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Limit (istəyə bağlı)</label>
            <input
              type="number"
              {...register("count")}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Yersiz satılan bilet növləri üçün (məs. masa, yaxud zal sxemi olmayan tədbirlər) bu say satışı məhdudlaşdırır: bu qədər satılandan sonra bilet bitir.
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center">
              <input
                id="isTable"
                type="checkbox"
                {...register("isTable")}
                onChange={(e) => {
                  setValue("isTable", e.target.checked);
                  // Seats and tables are different units, so start the list over
                  changeRows([]);
                }}
                className="mr-2"
              />
              <label htmlFor="isTable" className="text-sm font-medium text-gray-700">Bu bilet növü masadır</label>
            </div>
            {isTable && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Masa üçün yer sayı (nəfər)</label>
                <input
                  type="number"
                  {...register("capacity")}
                  className="mt-1 w-full border border-gray-300 p-2 rounded"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sxemdə hər masanın ətrafında bu qədər boz nöqtə (stul) göstərilir.
                </p>
              </div>
            )}
          </div>

          {seatSetup && seatSelection && (
            <fieldset className="border border-gray-200 rounded-lg p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-gray-700">{isTable ? "Masalar" : "Yerlər"}</legend>
              <p className="text-xs text-gray-500">
                Bu bilet növünün satdığı {isTable ? "masalar" : "yerlər"}: hər zona üçün {isTable ? "masa" : "yer"} nömrələrini yazın, məs.{" "}
                <span className="font-mono">1-6, 8</span>.
                {" "}Təyin etməsəniz, bilet xəritəsiz satılır və yuxarıdakı limitlə məhdudlaşır.
              </p>

              {seatRows.map((row, i) => {
                const zone = zones.find(({ code }) => code === row.zone);
                const maxNumber = zone ? Math.max(...zone.seats.map(({ number }) => number)) : null;
                return (
                  <div key={i}>
                    <div className="flex gap-2 items-center">
                      <select
                        value={row.zone}
                        onChange={(e) => updateRow(i, { zone: e.target.value })}
                        aria-label="Zona"
                        className="border border-gray-300 p-2 rounded bg-white w-36"
                      >
                        <option value="">Zona seçin</option>
                        {zones.map((option) => (
                          <option key={option.code} value={option.code} disabled={seatRows.some((other, j) => j !== i && other.zone === option.code)}>
                            {option.name || option.code}
                          </option>
                        ))}
                      </select>
                      <input
                        value={row.text}
                        onChange={(e) => updateRow(i, { text: e.target.value })}
                        aria-label="Yer nömrələri"
                        placeholder={maxNumber ? `1-${maxNumber}` : "1-16"}
                        className="flex-1 min-w-0 border border-gray-300 p-2 rounded font-mono text-sm"
                      />
                      <span className="text-xs text-gray-500 w-14 text-right tabular-nums">{seatSelection.rowCounts[i] || 0} yer</span>
                      <button
                        type="button"
                        onClick={() => changeRows((rows) => rows.filter((_, j) => j !== i))}
                        aria-label="Zonanı sil"
                        className="text-gray-400 hover:text-red-600 px-1"
                      >
                        ✕
                      </button>
                    </div>
                    {seatSelection.rowErrors[i] && <p className="text-xs text-red-600 mt-1">{seatSelection.rowErrors[i]}</p>}
                  </div>
                );
              })}

              {seatRows.length < zones.length && (
                <button
                  type="button"
                  onClick={() => changeRows((rows) => [...rows, { zone: "", text: "" }])}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Zona əlavə et
                </button>
              )}

              <div className="text-sm text-gray-700">Cəmi: <strong>{seatSelection.keys.length}</strong> yer</div>
              {movingText && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                  Diqqət: {movingText} bilet növündən bu növə keçəcək.
                </p>
              )}
              {seatSelection.removed > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                  {seatSelection.removed} yer bu növdən çıxarılır və satışdan çıxacaq.
                </p>
              )}
              {seatSelection.changed && !seatSetup.hasPlan && (
                <p className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                  Bu tədbirin hələ yer planı yoxdur: yadda saxlayanda plan hazırkı ayarlardan yaradılacaq, digər yerlərin qiyməti dəyişməyəcək.
                </p>
              )}
              <p className="text-xs text-gray-500">
                Bütün zalı sxemdə görmək və dəyişmək üçün:{" "}
                <Link to={`/dashboard/${eventId}/seat-plan`} className="text-blue-600 hover:underline">Yer planı</Link>
              </p>
            </fieldset>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register("isActive")}
              className="mr-2"
            />
            <label className="text-sm text-gray-700">Aktivdir</label>
          </div>

          {submitError && <p className="text-sm text-red-600">{submitError}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
          >
            {isEdit ? "Yadda saxla" : "Əlavə et"}
          </button>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default TicketTypeFormPage;
